import { css, StyleSheet } from 'aphrodite';
import { type ChangeEvent, type FC, useMemo, useState } from 'react';

import {
	FormFieldType,
	getContentHtmlStringValueFromRawRichTextContentState,
	type IRawRichTextContentState,
	type IRichContentEditorState,
	type IUser,
} from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import {
	convertRawRichTextContentStateToRichContentEditorState,
	createRichContentEditorStateWithText,
	ToolbarLight,
} from '../../../models/UiUtils';
import { RichContentDocumentEditor } from '../../../web/components/richContent/RichContentDocumentEditor';
import { UserCombobox, UserComboboxAvatarItem, UserComboboxStatus } from '../../../web/components/UserCombobox';
import { Button } from '../../../web/components-v2/Button';
import { Checkbox } from '../../../web/components-v2/Checkbox';
import {
	Combobox,
	ComboboxChip,
	ComboboxChipRemove,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemCheckboxIndicator,
	ComboboxList,
	ComboboxValue,
	useComboboxAnchor,
} from '../../../web/components-v2/Combobox';
import {
	ComboboxAvatarItem,
	ComboboxAvatarItemAvatar,
	ComboboxAvatarItemDescription,
	ComboboxAvatarItemTitle,
} from '../../../web/components-v2/Combobox/ComboboxAvatarItem';
import { DateInput } from '../../../web/components-v2/DateInput';
import { Input } from '../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import type { FocusedLeadContact } from '../data';
import { useLeadContacts } from '../data/useLeadContacts';
import { type DealFieldDef, isSavedNoteId } from './types';
import { useDealFieldOptions } from './useDealFieldOptions';
import { useDealNote } from './useDealNote';
import { useScheduleDemoMutation } from './useScheduleDemoMutation';

/**
 * Props for the pure `DealField` renderer.
 *
 * The renderer is intentionally controlled-only: callers own the `value` and
 * react to `onChange`. There is no internal state for the value, so the
 * component can be driven equally well by `useState` in `DealForm` or by a
 * future form library (react-hook-form, etc.) without changes to the
 * renderer.
 *
 * `companyId` and `associatedContactId` are only consumed by the
 * `MeetingScheduler` branch — the demo-link endpoint is keyed by the lead
 * (`lead/{companyId}/meetingScheduler`) and requires an associated contact.
 */
export type DealFieldProps = {
	field: DealFieldDef;
	value: unknown;
	onChange: (next: unknown) => void;
	error?: string;
	disabled?: boolean;
	/** Lead/company id — required for the MeetingScheduler field type. */
	companyId?: string;
	/** Associated contact id — required for the MeetingScheduler field type. */
	associatedContactId?: string;
	leadTimeZone?: string;
	/**
	 * Fires when the rep successfully clicks "Find Demo Spot" and the booking
	 * URL resolves. The form uses this to hide the demo-perform-date and
	 * demo-performer fields (the server populates them on rep return).
	 */
	onSchedulerEngaged?: (meetingId?: string) => void;
	/** Fires when the rep flips back to manual demo-time entry. */
	onSchedulerDisengaged?: () => void;
};

/**
 * Pure renderer for a single field of the Create-Deal form.
 *
 * Branches on `field.fieldType` and renders the appropriate components-v2
 * primitive (Input, Select, Checkbox, Combobox, DateInput, etc.) plus a
 * label and optional error message. Unsupported field types render a
 * placeholder; see the deal README for the v1 coverage matrix and
 * follow-up list.
 */
export const DealField: FC<DealFieldProps> = ({
	field,
	value,
	onChange,
	error,
	disabled,
	companyId,
	associatedContactId,
	leadTimeZone,
	onSchedulerEngaged,
	onSchedulerDisengaged,
}) => {
	if (field.isHidden) return null;

	const required = field.isOptional !== true;

	// MeetingScheduler renders its own controls without the standard label —
	// the legacy form also hides the label for this field type. The wrapper
	// is still rendered so error messages can attach.
	const showLabel = field.fieldType !== FormFieldType.MeetingScheduler;

	// Stable id used by `DealForm.handleCreate` to scroll the first
	// validation error into view (matches legacy
	// `DealDrawerContent.onActionClick` scroll-to-error behaviour). Keyed off
	// the always-present `field.id` — the same key the form state, errors, and
	// submit payload use.
	const rootId = `deal-field-${field.id}`;

	return (
		<div id={rootId} className={css(styles.root)}>
			{showLabel ? (
				<label className={css(styles.label)}>
					<span className={css(styles.labelText)}>
						{field.label}
						{required && (
							<span aria-hidden='true' className={css(styles.requiredAsterisk)}>
								*
							</span>
						)}
					</span>
					<FieldBody
						field={field}
						value={value}
						onChange={onChange}
						disabled={disabled}
						companyId={companyId}
						associatedContactId={associatedContactId}
						leadTimeZone={leadTimeZone}
						onSchedulerEngaged={onSchedulerEngaged}
						onSchedulerDisengaged={onSchedulerDisengaged}
					/>
				</label>
			) : (
				<FieldBody
					field={field}
					value={value}
					onChange={onChange}
					disabled={disabled}
					companyId={companyId}
					associatedContactId={associatedContactId}
					leadTimeZone={leadTimeZone}
					onSchedulerEngaged={onSchedulerEngaged}
					onSchedulerDisengaged={onSchedulerDisengaged}
				/>
			)}
			{error && <div className={css(styles.error)}>{error}</div>}
		</div>
	);
};

/**
 * Stringify the controlled value for primitives that expect a string. We
 * prefer the explicit `string` branch first so we don't accidentally call
 * `String(undefined)` → `'undefined'` for empty inputs.
 */
function asStringValue(value: unknown): string {
	if (typeof value === 'string') return value;
	if (value == null) return '';
	return String(value);
}

type FieldBodyProps = {
	field: DealFieldDef;
	value: unknown;
	onChange: (next: unknown) => void;
	disabled?: boolean;
	companyId?: string;
	associatedContactId?: string;
	leadTimeZone?: string;
	onSchedulerEngaged?: (meetingId?: string) => void;
	onSchedulerDisengaged?: () => void;
};

/**
 * Renders the actual input control for the field. Split out so the
 * `MeetingScheduler` branch (which manages its own internal toggle state)
 * can mount hooks alongside the rest of the field types.
 */
const FieldBody: FC<FieldBodyProps> = ({
	field,
	value,
	onChange,
	disabled,
	companyId,
	associatedContactId,
	leadTimeZone,
	onSchedulerEngaged,
	onSchedulerDisengaged,
}) => {
	const stringValue = asStringValue(value);

	switch (field.fieldType) {
		case FormFieldType.String:
		case FormFieldType.Email:
			return (
				<Input
					value={stringValue}
					onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
					placeholder={field.placeHolder}
					disabled={disabled}
				/>
			);

		case FormFieldType.Integer:
		case FormFieldType.Decimal:
		case FormFieldType.Money:
			return (
				<Input
					value={stringValue}
					onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
					placeholder={field.placeHolder}
					inputMode='numeric'
					disabled={disabled}
				/>
			);

		case FormFieldType.Text:
			return <RichTextFieldBody value={value} onChange={onChange} />;

		case FormFieldType.Note:
			return <NoteFieldBody value={value} onChange={onChange} />;

		case FormFieldType.Option:
			return (
				<OptionFieldBody
					field={field}
					value={stringValue}
					onChange={onChange}
					disabled={disabled}
					companyId={companyId}
				/>
			);

		case FormFieldType.MultipleOptions:
			return <MultipleOptionsFieldBody field={field} value={value} onChange={onChange} companyId={companyId} />;

		case FormFieldType.Boolean:
			return (
				<Checkbox
					checked={value === true}
					onCheckedChange={checked => onChange(checked === true)}
					disabled={disabled}
				/>
			);

		case FormFieldType.DateTime:
			return <DateTimeFieldBody value={stringValue} onChange={onChange} disabled={disabled} />;

		case FormFieldType.Date:
			return <DateInput value={stringValue} onChange={next => onChange(next)} />;

		case FormFieldType.User:
			return <UserFieldBody value={stringValue} onChange={onChange} placeholder={field.placeHolder} />;

		case FormFieldType.Contact:
			return (
				<ContactFieldBody
					value={stringValue}
					onChange={onChange}
					placeholder={field.placeHolder}
					companyId={companyId}
				/>
			);

		case FormFieldType.MeetingScheduler:
			return (
				<MeetingSchedulerFieldBody
					value={stringValue}
					onChange={onChange}
					companyId={companyId}
					associatedContactId={associatedContactId}
					leadTimeZone={leadTimeZone}
					onSchedulerEngaged={onSchedulerEngaged}
					onSchedulerDisengaged={onSchedulerDisengaged}
				/>
			);

		default:
			// FullAddress / FileUpload / Custom / Time / Name remain deferred —
			// render a clear placeholder so the rep can still submit (server
			// defaults will fill these for v1).
			return <div className={css(styles.unsupported)}>Field type not supported in v1: {field.fieldType}</div>;
	}
};

/* -------------------------------------------------------------------------- */
/*  Rich-text body                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Renders the legacy-parity rich-text editor for `Text` and `Note` field
 * types. Initialises content state from the (possibly HTML) string value
 * and reports changes back as an HTML string — matching what the legacy
 * `CustomFormViewModel.save` path POSTs.
 */
const RichTextFieldBody: FC<{
	value: unknown;
	onChange: (next: unknown) => void;
	initialContent?: IRawRichTextContentState;
}> = ({ value, onChange, initialContent }) => {
	// Initialise once from the incoming string — `RichContentDocumentEditor`
	// is uncontrolled internally, so re-deriving on every render would reset
	// the cursor. The initial state is captured in a ref-like memo keyed on
	// the initial value, not the live value.
	const initialState = useMemo(
		() =>
			initialContent
				? convertRawRichTextContentStateToRichContentEditorState(initialContent)
				: createRichContentEditorStateWithText(asStringValue(value)),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	return (
		<RichContentDocumentEditor
			autoFocus={false}
			className={css(styles.richEditor)}
			config={{
				toolbar: ToolbarLight,
				minHeight: 120,
				maxHeight: 280,
			}}
			contentState={initialState}
			onContentStateChanged={(state: IRichContentEditorState) => {
				onChange(getContentHtmlStringValueFromRawRichTextContentState(state.getRawRichTextContent()));
			}}
		/>
	);
};

const NoteFieldBody: FC<{ value: unknown; onChange: (next: unknown) => void }> = ({ value, onChange }) => {
	// Captured at mount: once the rep edits, the value becomes HTML, and switching
	// branches then would remount the editor and drop the cursor.
	const [savedNoteId] = useState(() => (isSavedNoteId(value) ? value : undefined));
	if (!savedNoteId) {
		return <RichTextFieldBody value={value} onChange={onChange} />;
	}
	return <SavedNoteFieldBody noteId={savedNoteId} onChange={onChange} />;
};

const SavedNoteFieldBody: FC<{ noteId: string; onChange: (next: unknown) => void }> = ({ noteId, onChange }) => {
	const { data: note, isLoading } = useDealNote(noteId);

	if (isLoading) {
		return <div className={css(styles.noteState)}>Loading…</div>;
	}
	// Not rendering the editor keeps the note id as the value, so a submit leaves the saved note untouched.
	// Checks data rather than `error`: a failed background refetch keeps `data` and must not unmount the editor.
	if (!note) {
		return <div className={css(styles.noteState)}>Could not load the saved note.</div>;
	}
	return <RichTextFieldBody value='' onChange={onChange} initialContent={note.content} />;
};

/* -------------------------------------------------------------------------- */
/*  Date + time composite                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Splits the controlled ISO-ish string into its date + time halves so the
 * rep can edit each independently. On any change, recomposes a full ISO-8601
 * UTC string (the legacy contract for `DateTime` fields).
 *
 * Timezone defaults to the browser's; a follow-up may surface an explicit
 * `TimezonePicker` for parity with the legacy `AidaDayPicker`.
 */
const DateTimeFieldBody: FC<{ value: string; onChange: (next: unknown) => void; disabled?: boolean }> = ({
	value,
	onChange,
	disabled,
}) => {
	const browserTimeZone = useMemo(() => {
		try {
			return Intl.DateTimeFormat().resolvedOptions().timeZone;
		} catch {
			return undefined;
		}
	}, []);

	const { date, time } = useMemo(() => splitIsoDateTime(value), [value]);

	const emit = (nextDate: string, nextTime: string) => {
		onChange(composeIsoDateTime(nextDate, nextTime));
	};

	return (
		<div className={css(styles.dateTimeWrap)}>
			<div className={css(styles.dateTimeRow)}>
				<div className={css(styles.dateCol)}>
					<DateInput value={date} onChange={next => emit(next, time)} />
				</div>
				<div className={css(styles.timeCol)}>
					<Input
						type='time'
						value={time}
						onChange={(e: ChangeEvent<HTMLInputElement>) => emit(date, e.target.value)}
						disabled={disabled}
					/>
				</div>
			</div>
			{browserTimeZone ? <span className={css(styles.tzHint)}>{browserTimeZone}</span> : null}
		</div>
	);
};

// Interpret the inputs as LOCAL wall clock; keep symmetric with splitIsoDateTime or the time drifts by the UTC offset on each edit.
export function composeIsoDateTime(date: string, time: string): string {
	if (!date) return '';
	const t = time || '00:00';
	// Known limitation: a wall-clock time that doesn't exist on a DST
	// spring-forward day (e.g. 02:30 in US Eastern) is normalized by `Date` to
	// the next valid instant (03:30). Acceptable here — demos aren't booked in
	// that one nonexistent hour, and avoiding it would require a full TZ library.
	const composed = new Date(`${date}T${t}:00`);
	if (Number.isNaN(composed.getTime())) return `${date}T${t}`;
	return composed.toISOString();
}

// Route zone-less strings to the slice branch and zoned ones through Date — slicing a zoned value shifts the display by the UTC offset.
export function splitIsoDateTime(value: string): { date: string; time: string } {
	if (!value) return { date: '', time: '' };
	// Zone-less datetime-local (optionally with seconds) — slice directly.
	if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value)) {
		return { date: value.slice(0, 10), time: value.slice(11, 16) };
	}
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return { date: '', time: '' };
	const pad = (n: number) => String(n).padStart(2, '0');
	const date = `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
	const time = `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
	return { date, time };
}

/* -------------------------------------------------------------------------- */
/*  Option single-select                                                      */
/* -------------------------------------------------------------------------- */

type SimpleOption = { id: string; label: string };

/**
 * Resolve the option list for an `Option` / `MultipleOptions` field.
 *
 * The form schema response (`lead/{companyId}/CreateDeal`) usually
 * returns these field types with `options` *empty*; the choices live
 * behind a per-field paginated endpoint
 * (`lead/{companyId}/CreateDeal/Field/{fieldId}`) which the legacy
 * `FormFieldViewModel.loadOptions` calls during mount. We mirror that
 * here through `useDealFieldOptions`. When the schema *does* include
 * inline options (some custom-form deployments) we use them directly and
 * skip the network round-trip.
 */
function useResolvedFieldOptions(
	field: DealFieldDef,
	companyId: string | undefined
): { options: SimpleOption[]; isLoading: boolean } {
	const inlineOptions = field.options ?? [];
	const hasInline = inlineOptions.length > 0;
	const lazy = useDealFieldOptions(hasInline ? undefined : companyId, hasInline ? undefined : field.id);
	if (hasInline) {
		return { options: inlineOptions, isLoading: false };
	}
	return { options: lazy.options, isLoading: lazy.isLoading };
}

/**
 * Single-select via `Select`. Lazy-loads choices through the per-field
 * options endpoint when the schema doesn't ship them inline. We
 * intentionally pass `value={stringValue}` (an empty string is a
 * perfectly valid controlled value) — passing `undefined` makes Base UI
 * treat the Select as uncontrolled, then flip it to controlled on first
 * pick, which raises a console warning.
 */
const OptionFieldBody: FC<{
	field: DealFieldDef;
	value: string;
	onChange: (next: unknown) => void;
	disabled?: boolean;
	companyId?: string;
}> = ({ field, value, onChange, disabled, companyId }) => {
	const { options, isLoading } = useResolvedFieldOptions(field, companyId);
	const selected = options.find(o => o.id === value);
	const placeholder = isLoading ? 'Loading…' : (field.placeHolder ?? '--');
	return (
		<Select value={value} onValueChange={onChange} disabled={disabled || isLoading}>
			<SelectTrigger>
				<SelectValue placeholder={placeholder}>{selected?.label}</SelectValue>
			</SelectTrigger>
			<SelectContent>
				{options.map(o => (
					<SelectItem key={o.id} value={o.id}>
						{o.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
};

/* -------------------------------------------------------------------------- */
/*  MultipleOptions multi-select                                              */
/* -------------------------------------------------------------------------- */

/**
 * True multi-select via `Combobox` in `multiple` mode + `ComboboxChips`.
 * The controlled value is a comma-joined string of ids — matching the
 * legacy `selectedOptions.map(o => o.dataContext.id).join(',')` contract
 * (see legacy `DealField/index.tsx:259`).
 */
const MultipleOptionsFieldBody: FC<{
	field: DealFieldDef;
	value: unknown;
	onChange: (next: unknown) => void;
	companyId?: string;
}> = ({ field, value, onChange, companyId }) => {
	const { options } = useResolvedFieldOptions(field, companyId);
	const stringValue = asStringValue(value);
	const selectedIds = useMemo(() => stringValue.split(',').filter(Boolean), [stringValue]);
	const selectedItems = useMemo(
		() => selectedIds.map(id => options.find(o => o.id === id)).filter((o): o is SimpleOption => Boolean(o)),
		[selectedIds, options]
	);
	const anchorRef = useComboboxAnchor();

	return (
		<Combobox
			multiple
			items={options}
			value={selectedItems}
			onValueChange={next => {
				const ids = (next as SimpleOption[]).map(o => o.id);
				onChange(ids.join(','));
			}}
			isItemEqualToValue={(a, b) => (a as SimpleOption).id === (b as SimpleOption).id}
			itemToStringValue={item => (item as SimpleOption).id}
			itemToStringLabel={item => (item as SimpleOption).label}
		>
			<ComboboxChips ref={anchorRef}>
				<ComboboxValue>
					{(selected: SimpleOption[]) => (
						<>
							{selected.map(o => (
								<ComboboxChip key={o.id} aria-label={o.label}>
									{o.label}
									<ComboboxChipRemove />
								</ComboboxChip>
							))}
							<ComboboxChipsInput placeholder={selected.length > 0 ? '' : (field.placeHolder ?? 'Select…')} />
						</>
					)}
				</ComboboxValue>
			</ComboboxChips>
			<ComboboxContent anchor={anchorRef}>
				<ComboboxEmpty>No options</ComboboxEmpty>
				<ComboboxList>
					{(option: SimpleOption) => (
						<ComboboxItem key={option.id} value={option}>
							<ComboboxItemCheckboxIndicator />
							{option.label}
						</ComboboxItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};

/* -------------------------------------------------------------------------- */
/*  User combobox                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Wraps the pre-built `UserCombobox` (a `Combobox` over
 * `useUserAutocompleteQuery`) and reduces the result to a user id string —
 * which is what `CustomFormViewModel.save` POSTs for `User`-typed fields.
 */
const UserFieldBody: FC<{ value: string; onChange: (next: unknown) => void; placeholder?: string }> = ({
	value,
	onChange,
	placeholder,
}) => {
	return (
		<UserCombobox id={value || undefined} onValueChange={(user: IUser | null) => onChange(user?.id ?? '')}>
			<ComboboxInput placeholder={placeholder ?? 'Search users…'} showClear={!!value} />
			<ComboboxContent>
				<UserComboboxStatus />
				<ComboboxList>{(user: IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}</ComboboxList>
			</ComboboxContent>
		</UserCombobox>
	);
};

/* -------------------------------------------------------------------------- */
/*  Contact combobox                                                          */
/* -------------------------------------------------------------------------- */

// Scope the picker to the LEAD's own contacts — the backend rejects an off-lead contact with "Invalid contact".
const ContactFieldBody: FC<{
	value: string;
	onChange: (next: unknown) => void;
	placeholder?: string;
	companyId?: string;
}> = ({ value, onChange, placeholder, companyId }) => {
	const contactsQuery = useLeadContacts(companyId);
	const contacts = contactsQuery.data ?? [];
	const selected = contacts.find(c => c.id === value) ?? null;
	// While the lead's contacts load, a pre-populated id (e.g. Update Deal mode)
	// has no matching object yet — surface "Loading…" so the field doesn't read
	// as empty and tempt the rep to re-pick.
	const inputPlaceholder = contactsQuery.isLoading && value ? 'Loading…' : (placeholder ?? 'Select a contact…');

	return (
		<Combobox
			items={contacts}
			value={selected}
			itemToStringLabel={(item: unknown) => (item as FocusedLeadContact)?.name ?? ''}
			isItemEqualToValue={(a, b) => (a as FocusedLeadContact)?.id === (b as FocusedLeadContact)?.id}
			onValueChange={(contact: unknown) => onChange((contact as FocusedLeadContact | null)?.id ?? '')}
		>
			<ComboboxInput placeholder={inputPlaceholder} showClear={!!value} />
			<ComboboxContent>
				<ComboboxEmpty>{contactsQuery.isLoading ? 'Loading…' : 'No contacts on this lead'}</ComboboxEmpty>
				<ComboboxList>
					{(contact: FocusedLeadContact) => (
						<ComboboxAvatarItem key={contact.id} value={contact}>
							<ComboboxAvatarItemAvatar
								imageUrl={contact.imageUrl ?? ''}
								fallbackText={contactInitials(contact.name)}
							/>
							<ComboboxAvatarItemTitle>{contact.name}</ComboboxAvatarItemTitle>
							<ComboboxAvatarItemDescription>{contact.email ?? ''}</ComboboxAvatarItemDescription>
						</ComboboxAvatarItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};

function contactInitials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	const first = parts[0]?.[0] ?? '';
	const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
	return `${first}${last}`.toUpperCase();
}

/* -------------------------------------------------------------------------- */
/*  MeetingScheduler ("Find Demo Spot")                                       */
/* -------------------------------------------------------------------------- */

type SchedulingMode = 'auto' | 'manual';

/**
 * Renders the "Find Demo Spot" / "Enter Demo Time Manually" toggle pair.
 * Auto mode posts to `lead/{companyId}/meetingScheduler` (via
 * `useScheduleDemoMutation`) and opens the returned URL in a new tab.
 *
 * On a successful auto-scheduler click we fire `onSchedulerEngaged`; the
 * parent `DealForm` uses this to hide the demo-perform-date and
 * demo-performer fields (matching legacy `meetingSchedulerState ===
 * Automatic` field-hiding behaviour). Flipping back to manual fires
 * `onSchedulerDisengaged`.
 */
const MeetingSchedulerFieldBody: FC<{
	value: string;
	onChange: (next: unknown) => void;
	companyId?: string;
	associatedContactId?: string;
	leadTimeZone?: string;
	onSchedulerEngaged?: (meetingId?: string) => void;
	onSchedulerDisengaged?: () => void;
}> = ({ value, onChange, companyId, associatedContactId, leadTimeZone, onSchedulerEngaged, onSchedulerDisengaged }) => {
	const userSession = useUserSession();
	const [mode, setMode] = useState<SchedulingMode>(value ? 'auto' : 'manual');
	const [errorText, setErrorText] = useState<string | null>(null);
	const scheduleDemo = useScheduleDemoMutation();

	const onAutoClick = async () => {
		setErrorText(null);
		if (!companyId) {
			setErrorText('No lead selected — refresh the page and try again.');
			return;
		}
		if (!associatedContactId) {
			setErrorText('You must select a contact to use the demo scheduler.');
			return;
		}
		try {
			const browserTimeZone = (() => {
				try {
					return Intl.DateTimeFormat().resolvedOptions().timeZone;
				} catch {
					return undefined;
				}
			})();
			// Book in the LEAD's timezone, not the rep's — otherwise the UI shows the rep's zone while the meeting is created in the lead's.
			const userTimeZone = userSession?.user?.userPreferences?.timeZone;
			const timeZone = leadTimeZone || userTimeZone || browserTimeZone;
			const result = await scheduleDemo.mutateAsync({
				companyId,
				contactId: associatedContactId,
				timeZone,
			});
			setMode('auto');
			onChange(result.url);
			// Pass the booked meeting id up so the form sends it as the top-level
			// `meetingId` (legacy `form.ts:534-535`) instead of a demo-perform-date.
			onSchedulerEngaged?.(result.scheduledMeetingId);
			window.open(result.url, '_blank', 'noopener,noreferrer');
		} catch (err) {
			setErrorText('Could not load the demo scheduler link.');
			console.error('Demo scheduler link error:', err);
		}
	};

	const onManualClick = () => {
		setMode('manual');
		setErrorText(null);
		onChange('');
		onSchedulerDisengaged?.();
	};

	return (
		<div className={css(styles.schedulerRoot)}>
			{mode === 'manual' ? (
				<Button size='sm' onClick={onAutoClick} disabled={scheduleDemo.isPending}>
					{scheduleDemo.isPending ? 'Loading…' : 'Find Demo Spot'}
				</Button>
			) : (
				<Button size='sm' variant='secondary' onClick={onManualClick}>
					Enter Demo Time Manually
				</Button>
			)}
			{errorText ? <div className={css(styles.error)}>{errorText}</div> : null}
		</div>
	);
};

/* -------------------------------------------------------------------------- */
/*  Styles                                                                    */
/* -------------------------------------------------------------------------- */

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	label: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
	labelText: {
		fontSize: 12,
		fontWeight: 500,
		color: 'var(--ink-muted)',
	},
	requiredAsterisk: {
		// Smaller, accent-coloured asterisk — matches the legacy
		// `DealField/styles.ts` rule that selected the inner `<span>` with
		// `fontSize: '11px'` (the surrounding label is 14px). The asterisk is
		// purely decorative; the meaningful "required" comes from the input's
		// own semantics, so we set `aria-hidden` on the span at the call
		// site.
		marginLeft: 2,
		fontSize: 11,
		color: 'var(--accent)',
	},
	error: {
		fontSize: 11,
		color: 'var(--accent)',
	},
	unsupported: {
		fontSize: 12,
		color: 'var(--ink-muted)',
		fontStyle: 'italic',
		padding: '8px 10px',
		background: 'var(--bg-elev)',
		border: '1px dashed var(--border)',
		borderRadius: 6,
	},
	richEditor: {
		minHeight: 120,
		border: '1px solid var(--border)',
		borderRadius: 8,
		background: 'var(--bg)',
	},
	noteState: {
		color: 'var(--ink-muted)',
		fontSize: 12,
	},
	dateTimeWrap: {
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	dateTimeRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
	},
	dateCol: {
		// `DateInput` renders a components-v2 `ButtonGroup` whose default
		// width is `fit-content`. Inside this row the parent flex column
		// must (a) take 2 of every 3 horizontal units (so the date input
		// has room for the YYYY-MM-DD text) and (b) force the inner
		// ButtonGroup to fill 100% — otherwise the date portion collapses
		// to ~icon-width and the rep sees only the calendar button.
		flex: 2,
		minWidth: 0,
		':nth-child(1n) > *': {
			width: '100%',
		},
	},
	timeCol: {
		// Time inputs are short — bound the column width so the date
		// portion gets the bulk of the row, and pin a sensible min so the
		// `--:-- ⌚` indicator never gets squeezed out.
		flex: '0 0 auto',
		width: 130,
	},
	tzHint: {
		fontSize: 11,
		color: 'var(--ink-muted)',
		whiteSpace: 'nowrap',
	},
	schedulerRoot: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
});
